import { useCallback } from 'react'

import { Contact } from '@features/contacts/types/contact'
import { useAccounts } from '@modules-deprecated/app/accounts'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useTaxRates } from '@modules-deprecated/app/taxrates'

import { BillFormFieldsChangeSource } from '../../../../../enums/billFormFieldsChangeSource'
import { useFormContext } from '../../../../../hooks/useForm'
import { getCurrentLocale } from '../../../../../utils'
import { useBillForm } from '../contexts/billFormContext'
import { autoCompleteBillForm } from '../utils/autoCompleteBillForm'
import { BillForm, BillFormSchema, getDefaultValues } from '../utils/formData'
import { getAutoCompletedFormWithVendor } from '../utils/getAutoCompletedFormWithVendor'
import { useTrackAutocompletedFields } from '../utils/trackAutocompletedFields'

export const useVendorAutoComplete = () => {
  const { organization } = useUserOrganization()
  const locale = getCurrentLocale()
  const { getValues, reset: resetForm, setValue } = useFormContext<BillFormSchema>()
  const { accounts = [] } = useAccounts()
  const { taxRates = [] } = useTaxRates()
  const { trackAutocompletedFields } = useTrackAutocompletedFields()
  const { billFormView, formFieldsChangeSource, setFormFieldsChangeSource } = useBillForm()

  const autoCompleteWithVendorData = useCallback(
    async (vendor: Partial<Contact> | undefined, source: BillFormFieldsChangeSource) => {
      const billForm = getValues()
      let billFormUpdated: BillForm

      if (!vendor) {
        /* If we are here, vendor is being cleared. We need to get rid of previous data blocked by vendor */
        for (const line of billForm.billLines || []) {
          if (line.lockedByVendor) {
            line.lockedByVendor = false
          }
        }

        const defaultValuesForm = getDefaultValues()
        defaultValuesForm.billLines = billForm.billLines

        billFormUpdated = autoCompleteBillForm({
          billFormDefault: billForm,
          billFormOverrides: { ...defaultValuesForm, autoCompletedFields: billForm.autoCompletedFields },
          baseCurrencyId: organization?.baseCurrencyId,
        })
      } else {
        billFormUpdated = await getAutoCompletedFormWithVendor({
          accounts,
          baseCurrencyId: organization?.baseCurrencyId,
          billForm,
          locale,
          organizationId: organization?.id,
          organizationCountryCode: organization?.countryId || 'DK',
          taxRates,
          vendor,
        })
      }

      if (billFormUpdated?.billLines) {
        for (const [i, line] of billFormUpdated?.billLines.entries()) {
          trackAutocompletedFields({
            billFormView,
            billLines: billForm?.billLines?.[i],
            context: source,
            formFieldsChangeSource,
            setFormFieldsChangeSource,
            updatedBillLines: line,
          })
        }
      }

      resetForm(billFormUpdated, { keepDefaultValues: !!vendor })
      // Wait one frame before setting autoCompletedFields
      window.requestAnimationFrame(() => {
        setValue('autoCompletedFields', billFormUpdated.autoCompletedFields)
      })
    },
    [
      accounts,
      billFormView,
      formFieldsChangeSource,
      getValues,
      locale,
      organization?.baseCurrencyId,
      organization?.countryId,
      organization?.id,
      resetForm,
      setValue,
      setFormFieldsChangeSource,
      taxRates,
      trackAutocompletedFields,
    ],
  )

  return { autoCompleteWithVendorData }
}
